import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import apiClient from './services/apiClient';
import websocketService from './services/websocketService';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Loading } from './components/ui/Button';
import { SEO } from './components/SEO';
import { PUBLIC_INFORMATIONAL_PATHS } from './config/publicSeoRoutes';

const lazyNamed = <T extends Record<string, any>, K extends keyof T>(
  importer: () => Promise<T>,
  key: K
) => lazy(() => importer().then((module) => ({ default: module[key] as React.ComponentType<any> })));

const lazyDefault = <T extends { default: React.ComponentType<any> }>(importer: () => Promise<T>) =>
  lazy(() => importer().then((module) => ({ default: module.default })));

const Dashboard = lazyNamed(() => import('./components/pages/ModernDashboard'), 'ModernDashboard');
const SchoolsPage = lazyNamed(() => import('./components/pages/ModernSchoolsPage'), 'ModernSchoolsPage');
const VerificationPage = lazyNamed(() => import('./components/pages/Verification'), 'VerificationPage');
const HistoryPage = lazyNamed(() => import('./components/pages/History'), 'HistoryPage');
const LoginPage = lazyNamed(() => import('./components/pages/Login'), 'LoginPage');
const StatisticsLoginPage = lazyNamed(() => import('./components/pages/StatisticsLogin'), 'StatisticsLoginPage');
const StatisticsPage = lazyNamed(() => import('./components/pages/Statistics'), 'StatisticsPage');
const RoleSelectionPage = lazyNamed(() => import('./components/pages/RoleSelection'), 'RoleSelectionPage');
const ForgotPasswordPage = lazyNamed(() => import('./components/pages/ForgotPassword'), 'ForgotPasswordPage');
const ResetPasswordPage = lazyNamed(() => import('./components/pages/ResetPassword'), 'ResetPasswordPage');
const ProjectsPage = lazyNamed(() => import('./components/pages/Projects'), 'ProjectsPage');
const ProjectDetailPage = lazyNamed(() => import('./components/pages/ProjectDetail'), 'ProjectDetailPage');
const ProjectTrackerDetailPage = lazyNamed(() => import('./components/pages/ProjectTrackerDetail'), 'ProjectTrackerDetailPage');
const SchoolProfilePage = lazyNamed(() => import('./components/pages/SchoolProfile'), 'SchoolProfilePage');
const LandingPage = lazyNamed(() => import('./components/pages/Landing'), 'LandingPage');
const ShopPage = lazyNamed(() => import('./components/pages/Shop'), 'ShopPage');
const CartPage = lazyNamed(() => import('./components/pages/Cart'), 'CartPage');
const ProductDetailPage = lazyNamed(() => import('./components/pages/ProductDetail'), 'ProductDetailPage');
const MealDistributionForm = lazyNamed(() => import('./components/pages/MealDistributionForm'), 'MealDistributionForm');
const MealDistributionCardPreviewPage = lazyNamed(() => import('./components/pages/MealDistributionCardPreview'), 'MealDistributionCardPreviewPage');
const SchoolTrackerChooser = lazyNamed(() => import('./components/pages/SchoolTrackerChooser'), 'SchoolTrackerChooser');
const TrackerEvidenceForm = lazyNamed(() => import('./components/pages/TrackerEvidenceForm'), 'TrackerEvidenceForm');
const ProjectActivityEvidenceForm = lazyNamed(() => import('./components/pages/ProjectActivityEvidenceForm'), 'ProjectActivityEvidenceForm');
const AwarenessRoleSelectionPage = lazyDefault(() => import('./components/pages/AwarenessRoleSelection'));
const DispatchTraceabilityPage = lazyNamed(() => import('./components/pages/DispatchTraceability'), 'DispatchTraceabilityPage');
const DispatchPage = lazyNamed(() => import('./components/pages/Dispatch'), 'DispatchPage');

const preloadAuthenticatedPages = () => {
  const preloads = [
    () => import('./components/pages/ModernDashboard'),
    () => import('./components/pages/ModernSchoolsPage'),
    () => import('./components/pages/Projects'),
    () => import('./components/pages/ProjectDetail'),
    () => import('./components/pages/ProjectTrackerDetail'),
    () => import('./components/pages/History'),
    () => import('./components/pages/Verification'),
    () => import('./components/pages/Dispatch'),
  ];

  for (const preload of preloads) {
    void preload();
  }
};

const Logo: React.FC<{ className?: string }> = ({ className = '' }) => (
  <img src="/images/agrozee-logo.png" alt="Agrozee Organics" className={className} />
);

const Icon: React.FC<{ name: string }> = ({ name }) => {
  const paths: Record<string, string> = {
    dashboard: 'M4 11.5L12 5l8 6.5M6.5 10v9h11v-9M10 19v-5h4v5',
    menu: 'M4 6h16M4 12h16M4 18h7',
    live: 'M13 10V3L4 14h7v7l9-11h-7z',
    history: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
    programs:
      'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10',
    schools:
      'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
    verification: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
    dispatch:
      'M3 7h11v8H3V7zm11 3h3l4 4v1h-7v-5zm-7 8a2 2 0 100-4 2 2 0 000 4zm10 0a2 2 0 100-4 2 2 0 000 4zM7 18h8',
    logout: 'M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1',
    sun:
      'M12 3v1m0 16v1m9-9h-1m-16 0H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z',
    moon: 'M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z',
  };

  return (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" d={paths[name]} />
    </svg>
  );
};

const Footer: React.FC = () => (
  <footer className="mt-auto border-t border-black/20 bg-white/90 px-6 py-10 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.55)]">
    <div className="mx-auto grid max-w-6xl gap-8 md:grid-cols-2">
      <div>
        <img src="/images/agrozee-footer-logo.png" alt="Agrozee Organics" className="mb-4 h-20 w-auto" />
        <h4 className="mb-2 text-sm font-black text-black">Agrozee Organics Pvt. Ltd.</h4>
        <p className="text-xs leading-6 text-secondary">
          Gat No. 189, Bhawrapur Road, Golibar Maidan,
          <br />
          Near Uruli Kanchan, Pune-412202
        </p>
        <p className="mt-3 text-xs leading-6 text-secondary">
          <strong>Phone:</strong> +91 9209431336
          <br />
          <strong>Email:</strong> agrozeeorganics@gmail.com
        </p>
      </div>
      <div>
        <h4 className="mb-3 text-sm font-black text-primary">Subscribe to our emails</h4>
        <p className="mb-4 text-xs leading-6 text-secondary">Join our community for harvest updates and exclusive offers.</p>
        <div className="flex gap-2">
          <input className="form-control min-w-0 flex-1" placeholder="Email" type="email" />
          <button className="btn-brand px-4" type="button" aria-label="Subscribe">
            <span aria-hidden="true">→</span>
          </button>
        </div>
      </div>
    </div>
    <p className="mt-8 border-t border-light pt-6 text-center text-xs text-tertiary">
      © 2026, Agrozee Organics Pvt. Ltd. All Rights Reserved.
    </p>
  </footer>
);

const MainLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark-theme'));
  const [isHeaderScrolled, setIsHeaderScrolled] = useState(false);
  const [user, setUser] = useState<any>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setUser(apiClient.getUser());
  }, []);

  useEffect(() => {
    const onScroll = () => setIsHeaderScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const toggleTheme = () => {
    const next = !isDark;
    setIsDark(next);
    document.documentElement.classList.toggle('dark-theme', next);
    document.body.classList.toggle('dark-theme', next);
    localStorage.setItem('theme', next ? 'dark' : 'light');
  };

  const handleLogout = async () => {
    await apiClient.logout();
    navigate('/', { replace: true });
  };

  const isReadOnlyUser = apiClient.isReadOnlyUser();
  const navGroups = [
    {
      label: 'Main Control',
      items: [
        { label: 'Dashboard', href: '/dashboard', icon: 'dashboard' },
      ],
    },
    {
      label: 'Management',
      items: [
        { label: 'Projects', href: '/projects', icon: 'programs' },
        { label: 'Schools', href: '/schools', icon: 'schools' },
        { label: 'History', href: '/history', icon: 'history' },
        { label: 'Dispatch', href: '/dispatches', icon: 'dispatch' },
        ...(!isReadOnlyUser
          ? [{ label: 'Verification', href: '/verification', icon: 'verification' }]
          : []),
      ],
    },
  ];
  const navItems = navGroups.flatMap((group) => group.items);

  const isActive = (href: string) => {
    const [pathname, search = ''] = href.split('?');
    if (pathname !== location.pathname) return false;
    if (search) return location.search === `?${search}`;
    return location.search === '' || href === '/dashboard';
  };

  return (
    <div className={`relative overflow-hidden app-bg min-h-screen flex flex-col ${isDark ? 'dark-theme' : ''}`}>
      <div className="relative z-10 flex min-h-full flex-1 flex-col">
      {/* Top Header */}
      <header
        className={`fixed inset-x-0 top-0 z-50 h-20 border-b border-black/10 transition-all duration-300 ${
          isHeaderScrolled
            ? 'bg-white/20 shadow-xl backdrop-blur-xl'
            : 'bg-white/80 shadow-sm backdrop-blur-md'
        }`}
      >
        <div className="mx-auto flex h-full w-full max-w-[1600px]">
          <div className="hidden h-full w-[176px] shrink-0 items-center justify-center border-r border-[#e7ede3] bg-white md:flex">
            <Link to="/dashboard" className="flex items-center">
              <Logo className="h-10 w-auto object-contain" />
            </Link>
          </div>

          <div className="flex min-w-0 flex-1 items-center justify-between px-4 md:px-8 xl:px-10">
            <div className="flex items-center gap-4 md:gap-6">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="rounded-xl p-2.5 text-primary hover:bg-tertiary md:hidden"
                aria-label="Toggle menu"
              >
                <Icon name="menu" />
              </button>
              <Link to="/dashboard" className="flex items-center md:hidden">
                <Logo className="h-10 w-auto object-contain" />
              </Link>
            </div>

            <div className="flex items-center gap-5">
              <button
                onClick={toggleTheme}
                className="rounded-2xl border border-light p-2.5 text-tertiary transition-all hover:bg-tertiary hover:text-primary active:scale-95 sm:p-3"
                aria-label="Toggle theme"
              >
                <Icon name={isDark ? 'moon' : 'sun'} />
              </button>
              
              <div className="hidden items-center gap-3 rounded-[28px] border border-light bg-secondary px-4 py-2 shadow-sm md:flex">
                <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-[var(--brand-color)] text-xs font-black text-white shadow-md">
                  {user?.username?.charAt(0)?.toUpperCase() || 'A'}
                </div>
                <div className="leading-tight">
                  <p className="text-[12px] font-black text-primary">{user?.username || 'Admin'}</p>
                  <p className="text-[9px] font-bold uppercase tracking-widest text-[var(--accent-dark)]">{user?.role || 'System Admin'}</p>
                </div>
              </div>

              <button
                onClick={handleLogout}
                className="rounded-2xl border border-light p-2.5 text-tertiary transition-all hover:bg-[rgba(239,68,68,0.1)] hover:text-[var(--status-danger)] active:scale-95 sm:p-3"
                aria-label="Logout"
              >
                <Icon name="logout" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex flex-1 pt-20">
        {/* Sidebar */}
        <aside
          className={`fixed left-0 top-20 z-40 flex h-[calc(100vh-5rem)] w-[280px] flex-col border-r border-[#e7ede3] bg-white shadow-2xl shadow-[rgba(16,42,24,0.08)] transition-transform duration-300 md:static md:h-auto md:w-[176px] md:translate-x-0 md:shadow-none ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <button
            className="absolute right-4 top-4 rounded-lg p-2 text-tertiary md:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close menu"
          >
            ×
          </button>

          <nav className="flex-1 overflow-y-auto px-4 py-8 pt-14 md:px-4 md:py-10">
            <div className="flex flex-col gap-4">
              {navItems.map((item) => {
                const active = isActive(item.href);
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    title={item.label}
                    className={`group flex items-center gap-3 rounded-[18px] px-4 py-4 text-[11px] font-bold uppercase tracking-[0.16em] transition-all duration-300 ${
                      active
                        ? 'bg-[linear-gradient(135deg,#259634_0%,#43b748_100%)] text-white shadow-[0_14px_28px_rgba(46,125,50,0.28)]'
                        : 'text-[#5f7c6d] hover:bg-[#f5faf3] hover:text-[#1f4b2f]'
                    }`}
                  >
                    <div className={`flex h-5 w-5 shrink-0 items-center justify-center transition-transform duration-300 ${active ? 'scale-105' : 'group-hover:scale-105'}`}>
                      <Icon name={item.icon} />
                    </div>
                    <span className="leading-none">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </nav>
        </aside>

        {/* Main Content Area */}
        <div className="flex min-w-0 flex-1 flex-col">
          <main className="relative flex-1 bg-transparent">
            <div className="relative z-10 mx-auto max-w-7xl px-3 py-5 sm:px-4 sm:py-7 md:px-10 lg:px-12">
              <Outlet />
            </div>
          </main>
          <Footer />
        </div>
      </div>
      </div>
    </div>
  );
};

const ProtectedLayout: React.FC = () => {
  if (!apiClient.isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  if (apiClient.isStatisticsViewer()) {
    return <Navigate to="/statistics" replace />;
  }
  return <MainLayout />;
};


const PublicEntry: React.FC = () => {
  if (apiClient.isStatisticsViewer()) {
    return <Navigate to="/statistics" replace />;
  }
  if (apiClient.isCommandUser()) {
    return <Navigate to="/dashboard" replace />;
  }

  return <LandingPage />;
};

const App: React.FC = () => {
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const shouldUseDark = savedTheme === 'dark';
    document.documentElement.classList.toggle('dark-theme', shouldUseDark);
    document.body.classList.toggle('dark-theme', shouldUseDark);

    const initialize = async () => {
      const token = localStorage.getItem('access_token');
      setInitialized(true);
      if (token) {
        const schedulePreload = () => {
          preloadAuthenticatedPages();
        };

        if (navigator.onLine !== false) {
          websocketService.connect(token).catch((socketError) => {
            console.warn('WebSocket unavailable during startup:', socketError);
          });
        }

        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(schedulePreload, { timeout: 1500 });
        } else {
          window.setTimeout(schedulePreload, 500);
        }
      }
    };

    initialize();

    return () => {
      websocketService.disconnect();
    };
  }, []);

  if (!initialized) {
    return <Loading message="Initializing application..." fullScreen />;
  }

  return (
    <ErrorBoundary>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <SEO />
        <Suspense fallback={<Loading message="Loading page..." fullScreen />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/role-selection" element={<RoleSelectionPage />} />
            <Route path="/stats-login" element={<StatisticsLoginPage />} />
            <Route path="/statistics" element={<StatisticsPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/splash" element={<Navigate to="/" replace />} />
            <Route path="/landing" element={<Navigate to="/" replace />} />
            <Route path="/agrogya-club" element={<Navigate to="/arogya-club" replace />} />
            <Route path="/shop" element={<ShopPage />} />
            <Route path="/shop/:slug" element={<ProductDetailPage />} />
            <Route path="/cart" element={<CartPage />} />
            {PUBLIC_INFORMATIONAL_PATHS.map((path) => (
              <Route key={path} path={path} element={<LandingPage />} />
            ))}
            <Route path="/preview/meal-distribution-card" element={<MealDistributionCardPreviewPage />} />
            <Route path="/school-track/:schoolId" element={<SchoolTrackerChooser />} />
            <Route path="/school-track/:schoolId/awareness" element={<AwarenessRoleSelectionPage />} />
            <Route path="/school-track/:schoolId/activity/:activityId" element={<ProjectActivityEvidenceForm />} />
            <Route path="/school-track/:schoolId/:trackerType" element={<TrackerEvidenceForm />} />
            <Route path="/meal-form/:schoolId" element={<MealDistributionForm />} />
            <Route path="/dispatch/:dispatchId/traceability" element={<DispatchTraceabilityPage />} />

            <Route element={<ProtectedLayout />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/projects" element={<ProjectsPage />} />
              <Route path="/projects/:projectId/awareness" element={<AwarenessRoleSelectionPage />} />
              <Route path="/projects/:id/trackers/:trackerType" element={<ProjectTrackerDetailPage />} />
              <Route path="/projects/:id" element={<ProjectDetailPage />} />
              <Route path="/schools" element={<SchoolsPage />} />
              <Route path="/schools/:id" element={<SchoolProfilePage />} />
              <Route path="/history" element={<HistoryPage />} />
              <Route path="/dispatches" element={<DispatchPage />} />
              <Route path="/verification" element={apiClient.isReadOnlyUser() ? <Navigate to="/dashboard" replace /> : <VerificationPage />} />
            </Route>

            <Route path="/" element={<PublicEntry />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </Router>
    </ErrorBoundary>
  );
};

export default App;
